// Pure, locale-aware file-size formatter (contract 04 §5, §16). Greenfield — no legacy counterpart.
// Scales bytes into the largest fitting unit and renders the mantissa through the locale-aware
// `formatNumber`, so the decimal separator follows the locale: `formatFileSize(1536, 'en-US')` →
// `"1.5 KB"`, `(1536, 'de-DE')` → `"1,5 KB"`, `(1536, 'en-US', { binary: true })` → `"1.5 KiB"`.
// Nullish / `NaN` / non-finite input returns the `'-'` sentinel. Zero Vue, zero Pinia, zero network.

import { SENTINEL } from './createFormatter'
import { formatNumber } from './number'
import type { FormatFileSizeOptions } from './types'

const DECIMAL_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] as const
const BINARY_UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'] as const

export function formatFileSize(
  value: number | null | undefined,
  locale: string,
  options: FormatFileSizeOptions = {},
): string {
  if (value == null || typeof value !== 'number' || !Number.isFinite(value)) return SENTINEL

  const binary = options.binary ?? false
  const base = binary ? 1024 : 1000
  const units = binary ? BINARY_UNITS : DECIMAL_UNITS

  const sign = value < 0 ? '-' : ''
  let size = Math.abs(value)
  let exponent = 0
  while (size >= base && exponent < units.length - 1) {
    size /= base
    exponent += 1
  }

  const unit = units[exponent] ?? 'B'
  // Whole bytes render as integers; scaled units keep one fraction digit ("1.5 KB").
  const maxFrac = exponent === 0 ? 0 : 1
  return `${sign}${formatNumber(size, locale, { maxFrac })} ${unit}`
}
